/**
 * Data Backup and Synchronization System
 * Prevents data loss by maintaining local backups and intelligent sync
 */

const BACKUP_KEY_PREFIX = 'bookmark_backup_';
const SYNC_STATUS_KEY = 'sync_status';
const LAST_SYNC_KEY = 'last_sync_timestamp';

export class DataBackupManager {
  constructor(userEmail) {
    this.userEmail = userEmail;
    this.backupKey = `${BACKUP_KEY_PREFIX}${userEmail}`;
    this.syncStatusKey = `${SYNC_STATUS_KEY}_${userEmail}`;
    this.lastSyncKey = `${LAST_SYNC_KEY}_${userEmail}`;
  }

  /**
   * Save bookmarks to local backup
   */
  saveLocalBackup(bookmarks) {
    try {
      const backupData = {
        bookmarks: bookmarks,
        timestamp: Date.now(),
        count: bookmarks.length,
        userEmail: this.userEmail,
        version: '1.0'
      };

      localStorage.setItem(this.backupKey, JSON.stringify(backupData));
      console.log(`📦 Local backup saved: ${bookmarks.length} bookmarks`);
      
      return true;
    } catch (error) {
      console.error('❌ Failed to save local backup:', error);
      return false;
    }
  }

  /**
   * Load bookmarks from local backup
   */
  loadLocalBackup() {
    try {
      const backupData = localStorage.getItem(this.backupKey);
      if (!backupData) {
        return { bookmarks: [], count: 0, timestamp: null };
      }

      const parsed = JSON.parse(backupData);
      console.log(`📥 Local backup loaded: ${parsed.count} bookmarks from ${new Date(parsed.timestamp).toLocaleString()}`);
      
      return {
        bookmarks: parsed.bookmarks || [],
        count: parsed.count || 0,
        timestamp: parsed.timestamp,
        version: parsed.version
      };
    } catch (error) {
      console.error('❌ Failed to load local backup:', error);
      return { bookmarks: [], count: 0, timestamp: null };
    }
  }

  /**
   * Compare local and backend data
   */
  compareData(localBookmarks, backendBookmarks) {
    const localCount = localBookmarks.length;
    const backendCount = backendBookmarks.length;

    // Create maps for efficient comparison
    const localMap = new Map(localBookmarks.map(b => [b.url + b.title, b]));
    const backendMap = new Map(backendBookmarks.map(b => [b.url + b.title, b]));

    const onlyInLocal = localBookmarks.filter(b => !backendMap.has(b.url + b.title));
    const onlyInBackend = backendBookmarks.filter(b => !localMap.has(b.url + b.title));
    const common = localBookmarks.filter(b => backendMap.has(b.url + b.title));

    const comparison = {
      localCount,
      backendCount,
      onlyInLocal: onlyInLocal.length,
      onlyInBackend: onlyInBackend.length,
      common: common.length,
      localBookmarks: onlyInLocal,
      backendBookmarks: onlyInBackend,
      needsSync: onlyInLocal.length > 0,
      dataLoss: localCount > backendCount && backendCount === 0,
      recommendation: this.getRecommendation(localCount, backendCount, onlyInLocal.length)
    };

    console.log('🔍 Data Comparison:', comparison);
    return comparison;
  }

  getRecommendation(localCount, backendCount, onlyInLocal) {
    if (backendCount === 0 && localCount > 0) {
      return 'RESTORE_FROM_LOCAL'; // Backend empty, local has data
    } else if (localCount > backendCount && onlyInLocal > 0) {
      return 'SYNC_TO_BACKEND'; // Local has more/newer data
    } else if (backendCount > localCount) {
      return 'SYNC_FROM_BACKEND'; // Backend has more data
    } else {
      return 'IN_SYNC'; // Data is synchronized
    }
  }

  /**
   * Update sync status
   */
  updateSyncStatus(status, details = {}) {
    const syncStatus = {
      status: status, // 'synced', 'pending', 'conflict', 'error'
      timestamp: Date.now(),
      details: details
    };

    localStorage.setItem(this.syncStatusKey, JSON.stringify(syncStatus));
    localStorage.setItem(this.lastSyncKey, Date.now().toString());
  }

  /**
   * Get sync status
   */
  getSyncStatus() {
    try {
      const status = localStorage.getItem(this.syncStatusKey);
      return status ? JSON.parse(status) : { status: 'unknown', timestamp: null };
    } catch (error) {
      return { status: 'error', timestamp: null };
    }
  }

  /**
   * Get last sync timestamp
   */
  getLastSyncTime() {
    const timestamp = localStorage.getItem(this.lastSyncKey);
    return timestamp ? parseInt(timestamp) : null;
  }

  /**
   * Clear all backup data for user
   */
  clearBackup() {
    localStorage.removeItem(this.backupKey);
    localStorage.removeItem(this.syncStatusKey);
    localStorage.removeItem(this.lastSyncKey);
    console.log('🗑️ Local backup cleared');
  }

  /**
   * Get backup info for display
   */
  getBackupInfo() {
    const backup = this.loadLocalBackup();
    const syncStatus = this.getSyncStatus();
    const lastSync = this.getLastSyncTime();

    return {
      hasBackup: backup.count > 0,
      bookmarkCount: backup.count,
      backupDate: backup.timestamp ? new Date(backup.timestamp) : null,
      syncStatus: syncStatus.status,
      lastSyncDate: lastSync ? new Date(lastSync) : null,
      age: backup.timestamp ? Date.now() - backup.timestamp : null
    };
  }

  /**
   * Export backup data for manual restore
   */
  exportBackupData() {
    const backup = this.loadLocalBackup();
    if (backup.count === 0) {
      return null;
    }

    const exportData = {
      ...backup,
      exportedAt: Date.now(),
      version: '1.0'
    };

    return exportData;
  }
}

/**
 * Auto-save bookmarks whenever they change
 */
export const setupAutoBackup = (userEmail) => {
  if (!userEmail) return null;
  
  const backupManager = new DataBackupManager(userEmail);
  
  // Setup periodic backup (every 30 seconds if data changed)
  let lastBookmarkData = '';
  
  const autoSave = (bookmarks) => {
    if (!bookmarks || bookmarks.length === 0) return;
    
    const currentData = JSON.stringify(bookmarks);
    if (currentData !== lastBookmarkData) {
      backupManager.saveLocalBackup(bookmarks);
      lastBookmarkData = currentData;
    }
  };

  return { backupManager, autoSave };
};